/**
 * Multi-Day Fixed Strategy
 *
 * Handles multi-day fixed-duration trip selection for booking functionality.
 * The trip duration is determined by the experience configuration (not user choice).
 * User selects a start date; end date is derived from duration.
 *
 * Following SOLID principles:
 * - Single Responsibility: Only handles multi-day fixed selection logic
 * - Strategy Pattern: Interchangeable with SingleDayStrategy via same interface
 * - Liskov Substitution: Drop-in replacement for SingleDayStrategy
 *
 * Part of: MVP 1.4.2 - Multi-Day Booking Feature
 * Story: SCRUM-408 - Multi-Day Calendar Strategy & Date Range Selection
 * Location: apps/web/src/components/calendar/strategies/MultiDayFixedStrategy.js
 */

class MultiDayFixedStrategy {
  constructor({
    durationDays = 1,
    prepDays = 0,
    cleanupDays = 0,
    startDayRestrictions = null,
    availabilityData = {},
    minBookingHours = 48,
    bookingHorizonMonths = 12,
    tripStartTime = '06:00:00'
  } = {}) {
    this.durationDays = durationDays;
    this.prepDays = prepDays;
    this.cleanupDays = cleanupDays;
    this.startDayRestrictions = startDayRestrictions;
    this.availabilityData = availabilityData;
    this.minBookingHours = minBookingHours;
    this.bookingHorizonMonths = bookingHorizonMonths;
    this.tripStartTime = tripStartTime;

    // Parse trip start time
    const [hours, minutes, seconds] = this.tripStartTime.split(':').map(Number);
    this.startHour = hours;
    this.startMinute = minutes || 0;
    this.startSecond = seconds || 0;
  }

  /**
   * Process a date selection.
   * For multi-day fixed, user selects a start date; end date is derived.
   * Always replaces the previous selection.
   *
   * @param {Date} date - The date being selected (start date)
   * @param {Date[]} _currentSelection - Currently selected dates (ignored)
   * @returns {Date[]} New selection array with single start date
   */
  selectDate(date, _currentSelection) {
    return [date];
  }

  /**
   * Check if selection is complete (has one start date).
   *
   * @param {Date[]} selection - Current selection
   * @returns {boolean} True if exactly one date is selected
   */
  isSelectionComplete(selection) {
    return selection.length === 1;
  }

  /**
   * Validate the selection against booking rules.
   * For multi-day: validates start date + full trip range + bookends.
   *
   * @param {Date[]} selection - Dates to validate
   * @returns {{ isValid: boolean, error?: string }}
   */
  validate(selection) {
    if (selection.length === 0) {
      return { isValid: false, error: 'Please select a start date' };
    }

    const startDate = selection[0];
    const now = new Date();

    // Rule 1: Cannot book past dates
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(startDate);
    checkDate.setHours(0, 0, 0, 0);

    if (checkDate < today) {
      return { isValid: false, error: 'Cannot select past dates' };
    }

    // Rule 2: Must meet minimum advance booking requirement
    const tripDateTime = new Date(startDate);
    tripDateTime.setHours(this.startHour, this.startMinute, this.startSecond, 0);
    const hoursUntilTrip = (tripDateTime - now) / (1000 * 60 * 60);
    if (hoursUntilTrip < this.minBookingHours) {
      return {
        isValid: false,
        error: `Minimum ${this.minBookingHours} hours advance notice required`
      };
    }

    // Rule 3: Full trip must fit within booking horizon
    const endDate = this._getEndDate(startDate);
    const maxBookingDate = new Date(now);
    maxBookingDate.setMonth(maxBookingDate.getMonth() + this.bookingHorizonMonths);

    if (endDate > maxBookingDate) {
      return {
        isValid: false,
        error: `Trip end date exceeds ${this.bookingHorizonMonths}-month booking horizon`
      };
    }

    // Rule 4: Start day restrictions
    if (!this._isAllowedStartDay(startDate)) {
      return {
        isValid: false,
        error: 'This date does not meet start day requirements'
      };
    }

    // Rule 5: All trip days must be available
    if (Object.keys(this.availabilityData).length > 0) {
      for (let i = 0; i < this.durationDays; i++) {
        const tripDay = new Date(startDate);
        tripDay.setDate(tripDay.getDate() + i);
        const dateKey = this._toDateKey(tripDay);
        const info = this.availabilityData[dateKey];
        if (info && !info.bookable) {
          return {
            isValid: false,
            error: `Day ${i + 1} of the trip is not available`
          };
        }
      }
    }

    return { isValid: true };
  }

  /**
   * Human-readable display of the selection.
   * Same month: "March 15-19, 2026 (5 days)"
   * Cross month same year: "December 29 - January 2, 2026 (5 days)"
   * Cross year: "December 29, 2025 - January 2, 2026 (5 days)"
   *
   * @param {Date[]} selection - Selected dates
   * @returns {string}
   */
  getSelectionDisplay(selection) {
    if (selection.length === 0) {
      return 'Select a start date';
    }

    const startDate = selection[0];
    const endDate = this._getEndDate(startDate);

    const sameMonth = startDate.getMonth() === endDate.getMonth() &&
      startDate.getFullYear() === endDate.getFullYear();
    const sameYear = startDate.getFullYear() === endDate.getFullYear();

    if (this.durationDays === 1) {
      return this.formatDate(startDate);
    }

    if (sameMonth) {
      const month = startDate.toLocaleDateString('en-US', { month: 'long' });
      return `${month} ${startDate.getDate()}-${endDate.getDate()}, ${startDate.getFullYear()} (${this.durationDays} days)`;
    }

    if (sameYear) {
      const startStr = startDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
      const endStr = endDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
      return `${startStr} - ${endStr} (${this.durationDays} days)`;
    }

    const startStr = startDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    const endStr = endDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    return `${startStr} - ${endStr} (${this.durationDays} days)`;
  }

  /**
   * Get the selectable date range based on current selection.
   *
   * @param {Date[]} _selection - Current selection (unused)
   * @returns {{ minDate: Date, maxDate: Date }}
   */
  getSelectableRange(_selection) {
    const now = new Date();
    const minDate = this.getMinBookingDate();
    const maxDate = new Date(now);
    maxDate.setMonth(maxDate.getMonth() + this.bookingHorizonMonths);

    return { minDate, maxDate };
  }

  /**
   * Is a given date within the current trip range? (for range highlighting)
   *
   * @param {Date} date
   * @param {Date[]} selection - [startDate]
   * @returns {boolean}
   */
  isDateInRange(date, selection) {
    if (selection.length === 0) {
      return false;
    }

    const startDate = selection[0];
    const endDate = this._getEndDate(startDate);

    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    const checkStart = new Date(startDate);
    checkStart.setHours(0, 0, 0, 0);
    const checkEnd = new Date(endDate);
    checkEnd.setHours(0, 0, 0, 0);

    return checkDate >= checkStart && checkDate <= checkEnd;
  }

  /**
   * Format a date for display.
   * Example: "Wednesday, November 15, 2025"
   *
   * @param {Date} date
   * @returns {string}
   */
  formatDate(date) {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  }

  /**
   * Format date as short string.
   *
   * @param {Date} date
   * @returns {string}
   */
  formatDateShort(date) {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }

  /**
   * Clear selection state.
   */
  clearSelection() {
    // No internal state to clear
  }

  /**
   * Get minimum booking date.
   *
   * @returns {Date}
   */
  getMinBookingDate() {
    const now = new Date();
    const checkDate = new Date(now);
    checkDate.setDate(checkDate.getDate() + 1);
    checkDate.setHours(0, 0, 0, 0);

    for (let i = 0; i < 5; i++) {
      const tripDateTime = new Date(checkDate);
      tripDateTime.setHours(this.startHour, this.startMinute, this.startSecond, 0);
      const hoursUntilTrip = (tripDateTime - now) / (1000 * 60 * 60);
      if (hoursUntilTrip >= this.minBookingHours) {
        return checkDate;
      }
      checkDate.setDate(checkDate.getDate() + 1);
    }

    const fallback = new Date(now);
    fallback.setDate(fallback.getDate() + 2);
    fallback.setHours(0, 0, 0, 0);
    return fallback;
  }

  /**
   * Get maximum booking date (booking horizon).
   *
   * @returns {Date}
   */
  getMaxBookingDate() {
    const now = new Date();
    const maxDate = new Date(now);
    maxDate.setMonth(maxDate.getMonth() + this.bookingHorizonMonths);
    return maxDate;
  }

  /**
   * Is a specific date bookable as a START date?
   * Checks ALL of:
   * (a) guide available for ALL trip days
   * (b) start day restrictions satisfied
   * (c) prep/cleanup bookend days don't conflict
   * (d) full trip window fits within booking horizon
   *
   * @param {Date} date
   * @returns {boolean}
   */
  isDateBookable(date) {
    // Basic validation (past, advance notice, horizon)
    const validation = this.validate([date]);
    if (!validation.isValid) {
      return false;
    }

    // Check start day restrictions
    if (!this._isAllowedStartDay(date)) {
      return false;
    }

    // Check all trip days are available
    if (Object.keys(this.availabilityData).length > 0) {
      for (let i = 0; i < this.durationDays; i++) {
        const tripDay = new Date(date);
        tripDay.setDate(tripDay.getDate() + i);
        const dateKey = this._toDateKey(tripDay);
        const info = this.availabilityData[dateKey];
        if (info && !info.bookable) {
          return false;
        }
      }

      // Check prep days (before trip start)
      for (let i = 1; i <= this.prepDays; i++) {
        const prepDay = new Date(date);
        prepDay.setDate(prepDay.getDate() - i);
        const dateKey = this._toDateKey(prepDay);
        const info = this.availabilityData[dateKey];
        if (info && !info.bookable) {
          return false;
        }
      }

      // Check cleanup days (after trip end)
      const endDate = this._getEndDate(date);
      for (let i = 1; i <= this.cleanupDays; i++) {
        const cleanupDay = new Date(endDate);
        cleanupDay.setDate(cleanupDay.getDate() + i);
        const dateKey = this._toDateKey(cleanupDay);
        const info = this.availabilityData[dateKey];
        if (info && !info.bookable) {
          return false;
        }
      }
    }

    return true;
  }

  /**
   * Get tooltip text for a disabled date.
   *
   * @param {Date} date
   * @returns {string|null}
   */
  getDateDisabledReason(date) {
    // Start day restrictions
    if (!this._isAllowedStartDay(date)) {
      const dayNames = ['Sundays', 'Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays'];
      const allowedNames = this.startDayRestrictions.allowed_days
        .map(d => dayNames[d])
        .join(' and ');
      return `This experience starts on ${allowedNames} only`;
    }

    // Check if any trip day is unavailable
    if (Object.keys(this.availabilityData).length > 0) {
      for (let i = 0; i < this.durationDays; i++) {
        const tripDay = new Date(date);
        tripDay.setDate(tripDay.getDate() + i);
        const dateKey = this._toDateKey(tripDay);
        const info = this.availabilityData[dateKey];
        if (info && !info.bookable) {
          return `Day ${i + 1} of the ${this.durationDays}-day trip is unavailable`;
        }
      }

      // Check bookend conflicts
      for (let i = 1; i <= this.prepDays; i++) {
        const prepDay = new Date(date);
        prepDay.setDate(prepDay.getDate() - i);
        const dateKey = this._toDateKey(prepDay);
        const info = this.availabilityData[dateKey];
        if (info && !info.bookable) {
          return `Prep day ${i} before trip is unavailable`;
        }
      }

      const endDate = this._getEndDate(date);
      for (let i = 1; i <= this.cleanupDays; i++) {
        const cleanupDay = new Date(endDate);
        cleanupDay.setDate(cleanupDay.getDate() + i);
        const dateKey = this._toDateKey(cleanupDay);
        const info = this.availabilityData[dateKey];
        if (info && !info.bookable) {
          return `Cleanup day ${i} after trip is unavailable`;
        }
      }
    }

    return null;
  }

  // ─── Internal helpers ───────────────────────────────────────────

  /**
   * Get the end date for a given start date.
   * endDate = startDate + durationDays - 1
   *
   * @param {Date} startDate
   * @returns {Date}
   */
  _getEndDate(startDate) {
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + this.durationDays - 1);
    return endDate;
  }

  /**
   * Get the full blocked range including bookends.
   *
   * @param {Date} startDate
   * @returns {{ holdStart: Date, holdEnd: Date }}
   */
  _getFullBlockedRange(startDate) {
    const holdStart = new Date(startDate);
    holdStart.setDate(holdStart.getDate() - this.prepDays);

    const endDate = this._getEndDate(startDate);
    const holdEnd = new Date(endDate);
    holdEnd.setDate(holdEnd.getDate() + this.cleanupDays);

    return { holdStart, holdEnd };
  }

  /**
   * Check if a date satisfies start day restrictions.
   *
   * @param {Date} date
   * @returns {boolean}
   */
  _isAllowedStartDay(date) {
    if (!this.startDayRestrictions ||
        !this.startDayRestrictions.allowed_days ||
        this.startDayRestrictions.allowed_days.length === 0) {
      return true;
    }
    return this.startDayRestrictions.allowed_days.includes(date.getDay());
  }

  /**
   * Convert a Date to YYYY-MM-DD string for availability lookup.
   *
   * @param {Date} date
   * @returns {string}
   */
  _toDateKey(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}

export default MultiDayFixedStrategy;
